// Cart drawer product recommendations.
//
// Fetches Shopify's algorithmic "related" recommendations for up to three cart
// products (highest value first), merges them round-robin, drops duplicates and
// products already in the cart, then renders the result into the cart drawer.
//
// Defined globally (loaded from theme.liquid) so it re-runs whenever the cart
// drawer re-renders its markup via innerHTML on a cart change. Each render mounts
// a fresh instance, which fetches recommendations for the updated cart.

const DEFAULT_LIMIT = 6;
const PER_SEED_FETCH_LIMIT = 10;

class CartRecommendations extends HTMLElement {
  connectedCallback() {
    if (this._loaded) return;
    this._loaded = true;
    // Progressive enhancement: on any unexpected failure, remove the element
    // rather than leave a broken or empty recommendations block in the drawer.
    this._load().catch(() => this.remove());
  }

  async _load() {
    const seeds = this._parseList(this.getAttribute('seeds'));

    if (seeds.length === 0) {
      this.remove();
      return;
    }

    const excluded = new Set(this._parseList(this.getAttribute('exclude')));
    const limit = parseInt(this.getAttribute('limit'), 10) || DEFAULT_LIMIT;
    const sectionId = this.getAttribute('section-id');

    const documents = (await Promise.all(seeds.map((seed) => this._fetchSeed(seed, sectionId)))).filter(Boolean);

    // The first non-empty response provides the heading, controls and carousel shell.
    const skeleton = documents.map((doc) => doc.querySelector('.cart-recommendations__block')).find(Boolean);

    if (!skeleton) {
      this.remove();
      return;
    }

    const cardLists = documents.map((doc) => Array.from(doc.querySelectorAll('.horizontal-product')));
    const merged = this._merge(cardLists, excluded, limit);

    if (merged.length === 0) {
      this.remove();
      return;
    }

    skeleton.querySelector('.horizontal-product-list').replaceChildren(...merged);

    const controls = skeleton.querySelector('[data-recommendations-controls]');
    if (controls) controls.hidden = merged.length <= 1;

    this.replaceChildren(document.importNode(skeleton, true));
    this.hidden = false;
  }

  async _fetchSeed(seed, sectionId) {
    const url = `${Shopify.routes.root}recommendations/products?product_id=${encodeURIComponent(seed)}&intent=related&limit=${PER_SEED_FETCH_LIMIT}&section_id=${encodeURIComponent(sectionId)}`;

    try {
      const response = await fetch(url, { priority: 'low' });
      if (!response.ok) return null;
      return new DOMParser().parseFromString(await response.text(), 'text/html');
    } catch (error) {
      return null;
    }
  }

  // Round-robin across each seed's recommendations so the result mixes anchors
  // instead of being dominated by the first product.
  _merge(cardLists, excluded, limit) {
    const seen = new Set();
    const merged = [];
    const maxLength = cardLists.reduce((max, cards) => Math.max(max, cards.length), 0);

    for (let index = 0; index < maxLength && merged.length < limit; index++) {
      for (const cards of cardLists) {
        if (merged.length >= limit) break;

        const card = cards[index];
        if (!card) continue;

        const handle = this._handleOf(card);
        if (!handle || excluded.has(handle) || seen.has(handle)) continue;

        seen.add(handle);
        merged.push(card);
      }
    }

    return merged;
  }

  _handleOf(card) {
    const link = card.querySelector('a[href*="/products/"]');
    if (!link) return null;

    const path = link.getAttribute('href').split('?')[0].replace(/\/$/, '');
    const match = path.match(/\/products\/([^/]+)$/);
    return match ? match[1] : null;
  }

  _parseList(value) {
    return (value || '').split(',').map((item) => item.trim()).filter(Boolean);
  }
}

if (!window.customElements.get('cart-recommendations')) {
  window.customElements.define('cart-recommendations', CartRecommendations);
}
